import React from "react";
import { useNavigate } from "react-router-dom";
import { Award, Home as HomeIcon, RotateCcw, LogOut, Cloud, Flame, Crown } from "lucide-react";
import { getAvatar } from "../lib/constants";
import { resetProfile } from "../lib/storage";
import { useAuth } from "../contexts/AuthContext";

export const ProfileBar = ({ profile, onReset }) => {
  const navigate = useNavigate();
  const avatar = getAvatar(profile.avatarId);
  const { status, user, logout } = useAuth();
  const isCloud = status === "cloud";

  const handleReset = () => {
    if (window.confirm("¿Quieres reiniciar tu perfil? Perderás puntos y medallas.")) {
      resetProfile();
      if (onReset) onReset();
      navigate(isCloud ? "/perfil" : "/");
    }
  };

  const handleLogout = () => {
    const msg = isCloud
      ? "¿Cerrar sesión? Tu progreso queda guardado en tu cuenta."
      : "¿Cerrar sesión? Tu progreso como invitado se mantiene en este dispositivo.";
    if (window.confirm(msg)) {
      logout();
      // Clear local profile so the next user starts fresh
      try { localStorage.removeItem("mateaventura.profile"); } catch {}
      if (onReset) onReset();
      navigate("/", { replace: true });
    }
  };

  return (
    <div
      className="flex items-center justify-between gap-2 bg-white/85 backdrop-blur rounded-[2rem] border-4 border-white shadow-[0_10px_30px_-15px_rgba(0,0,0,0.15)] px-2.5 sm:px-6 py-2.5 sm:py-4"
      data-testid="profile-bar"
    >
      <button
        onClick={() => navigate("/dashboard")}
        className="flex items-center gap-2 sm:gap-3 group min-w-0 shrink"
        data-testid="profile-bar-home"
      >
        <div
          className="w-10 h-10 sm:w-14 sm:h-14 rounded-2xl overflow-hidden border-2 sm:border-4 border-[#FFD700] bg-[#FFF6CC] group-hover:scale-105 transition-transform shrink-0"
        >
          <img src={avatar?.url} alt={avatar?.name} className="w-full h-full object-cover" />
        </div>
        <div className="text-left hidden sm:block">
          <p className="text-xs uppercase tracking-wider font-bold text-[#5C5C60]">Hola</p>
          <p className="font-heading font-bold text-lg text-[#1C1C1E]" data-testid="profile-name">
            {profile.name}
          </p>
        </div>
      </button>

      <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
        <div
          className="flex items-center gap-1 sm:gap-2 bg-[#FFF6CC] border-2 border-[#FFD700] rounded-full px-2 sm:px-4 py-1.5 sm:py-2"
          data-testid="profile-points"
        >
          <span className="text-base sm:text-xl">⭐</span>
          <span className="font-heading font-bold text-sm sm:text-lg text-[#7A5C00]">
            {profile.points}
          </span>
        </div>
        <div
          className="flex items-center gap-1 sm:gap-1.5 bg-[#FFEEDC] border-2 border-[#FF9500] rounded-full px-2 sm:px-4 py-1.5 sm:py-2"
          data-testid="profile-streak"
          title={`Mejor racha: ${profile.streak?.bestStreak || 0} días`}
        >
          <Flame className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-[#FF6B00]" strokeWidth={2.5} fill="#FF9500" />
          <span className="font-heading font-bold text-sm sm:text-lg text-[#B05A00]">
            {profile.streak?.count || 0}
          </span>
        </div>
        <button
          onClick={() => navigate("/medallas")}
          className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 rounded-full w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-2 hover:bg-gray-50 transition-colors"
          data-testid="profile-medals-btn"
          aria-label="Ver medallas"
        >
          <Award className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF9500]" strokeWidth={3} />
          <span className="font-bold text-sm hidden sm:inline">
            {(profile.medals || []).length}
          </span>
        </button>
        {isCloud && (
          <button
            onClick={() => navigate("/mi-suscripcion")}
            className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white border-2 border-[#FFD700] hover:bg-[#FFF6CC] transition-colors"
            data-testid="profile-subscription-btn"
            aria-label="Mi Suscripción"
            title="Mi Suscripción"
          >
            <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF9500]" strokeWidth={2.5} />
          </button>
        )}
        <button
          onClick={() => navigate("/dashboard")}
          className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-white border-2 border-gray-200 hover:bg-gray-50 transition-colors"
          data-testid="profile-home-btn"
          aria-label="Inicio"
        >
          <HomeIcon className="w-5 h-5 text-[#1C1C1E]" strokeWidth={3} />
        </button>
        <button
          onClick={handleReset}
          className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-white border-2 border-gray-200 hover:bg-red-50 transition-colors"
          data-testid="profile-reset-btn"
          aria-label="Reiniciar perfil"
          title="Reiniciar perfil"
        >
          <RotateCcw className="w-5 h-5 text-[#5C5C60]" strokeWidth={3} />
        </button>
        {(isCloud || status === "guest") && (
          <button
            onClick={handleLogout}
            className="flex items-center justify-center sm:gap-2 bg-white border-2 border-[#FFCDD2] hover:bg-[#FFEAEA] rounded-full w-9 h-9 sm:w-auto sm:h-auto sm:px-4 sm:py-2 transition-colors font-bold text-sm text-[#FF6B6B]"
            data-testid="profile-logout-btn"
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={3} />
            <span className="hidden sm:inline">Cerrar Sesión</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileBar;
